// src/store/useProjectStore.ts
// Single source of truth dla wizard-a. Zustand, bez persistance (dodamy potem).

import { create } from 'zustand';
import type { EstimateItem, TradeEngine } from '../domain/estimate.types';
import type { PriceRegion } from '../domain/material.rates';
import { Estimate, BusinessRule, SpatialData } from '../domain/types';
import { type EngineResult, type ProfitLeak } from '../domain/estimate.types';
import {
  getRecommendedRemovalWarning,
  type RecommendedRemovalWarning,
} from '../domain/recommendationWarnings';
import {
  applyLaborRateMultiplier,
  getLaborRateMultiplier,
} from '../domain/simpleEstimateEngine';
import type { MaterialMode } from '../domain/materialMode';
import { sumMoney } from '../domain/money';
import { validateFromAIVision, calculateTotalLoss, formatLossMessage } from '../domain/rules';

// ---------- TYPY ----------

export type Branza = 'malarz' | 'lazienka' | 'brukarstwo' | 'ogolnobudowlany' | null;

export type WizardStep = 1 | 2 | 3 | 4 | 5;

export type MargePreset = 'tanio' | 'standard' | 'premium';
export type SettlementMode = 'no_vat' | 'vat_8' | 'vat_23';

export interface Gap {
  id: string;           // np. 'missing_grunt'
  severity: 'warning' | 'critical';
  title: string;        // 'Brak gruntowania'
  reason: string;       // 'Farba odparzy w 2 lata. Koszt naprawy ~5000 zĹ‚.'
  autoFixLabel: string; // 'DODAJ AUTO-FIX +520 zĹ‚'
  autoFixAmount: number;
  autoFixItem: Omit<EstimateItem, 'id'>; // co dopisaÄ‡ do koszyka
}

export interface ProjectData {
  // STEP 1
  branza: Branza;
  tradeEngine: TradeEngine | null;

  // STEP 2
  projectName: string;
  clientName: string;
  description: string;
  photoDataUrl: string | null;
  // Geometria (wypeĹ‚niana rÄ™cznie lub z AI)
  obwod: number | null;
  wysokosc: number | null;
  powPodlogi: number | null;
  projectMode: 'room' | 'apartment';
  roomCount: number | null;
  region: string;
  cityLocation: string;
  useCustomLaborRate: boolean;
  customLaborRateRbg: number | null;
  length: number | null;
  width: number | null;
  height: number | null;
  powScianNetto: number | null; // (O Ă— h) - otwory + glify
  otworyStandardoweSzt: number | null;
  otworyDuzeM2: number | null;
  standardWindowCount: number | null;
  bigWindowsArea: number | null;
  glifyMb: number | null; // metry bieĹĽÄ…ce glifĂłw
  // KNR: liczba drzwi do odliczenia (2mÂ˛/szt)
  doorCount: number | null;
  // PN-B: liczba otworĂłw (tylko w SpatialData dla AI)
  windowCount?: number;
  // AI output
  aiAnalysisDone: boolean;
  aiConfidence: number; // 0-1 pewnoĹ›Ä‡ AI
  aiDetectedGaps: string[]; // flagi z AI: missing_grunt, missing_hydro, etc.
  surfaceCondition: string; // goĹ‚y_tynk, grunt, gĹ‚adka, etc.
  isOutdoor: boolean;
  bathroomTilesAreaM2: number | null;
  bathroomHydroAreaM2: number | null;
  bathroomWaterPoints: number | null;
  bathroomElectricPoints: number | null;
  bathroomDemolitionItems: number | null;
  bathroomGrooveLengthM: number | null;
  bathroomTileRemovalAreaM2: number | null;
  pavingAreaM2: number | null;
  pavingStoneThicknessCm: number | null;
  pavingEdgeLengthMb: number | null;
  pavingCurbLengthMb: number | null;
  pavingExcavationDepthCm: number | null;
  pavingSubbaseThicknessCm: number | null;
  pavingBeddingThicknessCm: number | null;
  pavingWasteRemovalM3: number | null;
  pavingCuttingLengthMb: number | null;
  pavingHasGeotextile: boolean;
  pavingMachineAccess: boolean;
  pavingCrewSize: number | null;
  edgeLengthM: number | null;
  baseThicknessCm: number | null;
  beddingThicknessCm: number | null;
  crewSize: number | null;
  // Tryb RB: prace trudne
  requiresRB: boolean;
  rbHours: number | null;

  // Domain model integration
  estimate: Estimate | null; // PeĹ‚ny model domenowy
  currentEstimate: EngineResult | null;
  profitLeaks: ProfitLeak[];
  dismissedProfitLeakIds: string[];
  pendingRecommendedRemoval: RecommendedRemovalWarning | null;
  businessRules: BusinessRule[]; // Aktywne reguĹ‚y Tarczy MarĹĽy
  totalLossAmount: number; // Suma strat w PLN

  // STEP 3
  items: EstimateItem[];
  detectedGaps: Gap[];      // braki wykryte przez silnik (grunt, glify, etc.)
  appliedGapIds: string[];  // id brakĂłw juĹĽ dodanych do koszyka

  // STEP 4
  margePreset: MargePreset;
  margeValue: number; // legacy: separate margin is no longer added to client total
  laborRateMultiplier: number;
  aiAdded: number;
  selectedPriceRegion: PriceRegion;
  userMaterialOverrides: Partial<Record<string, number>>;
  materialMode: MaterialMode;
  settlementMode: SettlementMode;
}

interface ProjectStore extends ProjectData {
  // nawigacja
  step: WizardStep;
  goNext: () => void;
  goBack: () => void;
  goTo: (step: WizardStep) => void;
  reset: () => void;

  // dostÄ™pnoĹ›Ä‡ AI
  isAIAvailable: boolean;
  setAIAvailable: (v: boolean) => void;

  // settery step 1
  setBranza: (b: Branza) => void;

  // settery step 2
  setProjectName: (v: string) => void;
  setClientName: (v: string) => void;
  setDescription: (v: string) => void;
  setPhoto: (dataUrl: string | null) => void;
  setGeometry: (g: Partial<Pick<ProjectData, 'obwod' | 'wysokosc' | 'powPodlogi' | 'projectMode' | 'roomCount' | 'region' | 'cityLocation' | 'useCustomLaborRate' | 'customLaborRateRbg' | 'length' | 'width' | 'height' | 'otworyStandardoweSzt' | 'otworyDuzeM2' | 'standardWindowCount' | 'bigWindowsArea' | 'doorCount' | 'windowCount' | 'requiresRB' | 'rbHours' | 'surfaceCondition' | 'isOutdoor'>>) => void;
  setBathroomData: (data: Partial<Pick<ProjectData, 'bathroomTilesAreaM2' | 'bathroomHydroAreaM2' | 'bathroomWaterPoints' | 'bathroomElectricPoints' | 'bathroomDemolitionItems' | 'bathroomGrooveLengthM' | 'bathroomTileRemovalAreaM2'>>) => void;
  setPavingData: (data: Partial<Pick<ProjectData, 'pavingAreaM2' | 'pavingStoneThicknessCm' | 'pavingEdgeLengthMb' | 'pavingCurbLengthMb' | 'pavingExcavationDepthCm' | 'pavingSubbaseThicknessCm' | 'pavingBeddingThicknessCm' | 'pavingWasteRemovalM3' | 'pavingCuttingLengthMb' | 'pavingHasGeotextile' | 'pavingMachineAccess' | 'pavingCrewSize' | 'edgeLengthM' | 'baseThicknessCm' | 'beddingThicknessCm' | 'crewSize'>>) => void;
  setAiAnalysisDone: (v: boolean) => void;

  // settery step 3 (koszyk + braki)
  setItems: (items: EstimateItem[]) => void;
  addItem: (item: EstimateItem) => void;
  removeItem: (id: string) => void;
  setDetectedGaps: (gaps: Gap[]) => void;
  applyGap: (gapId: string) => void;      // klik w "Dodaj auto-fix"
  dismissGap: (gapId: string) => void;    // user odrzuca braki

  // settery step 4
  setMargePreset: (p: MargePreset) => void;
  setMargeValue: (v: number) => void;
  setAiAdded: (value: number | ((current: number) => number)) => void;
  setPriceRegion: (region: PriceRegion) => void;
  setMaterialOverride: (materialId: string, price: number) => void;
  clearMaterialOverride: (materialId: string) => void;
  setMaterialMode: (mode: MaterialMode) => void;
  setSettlementMode: (mode: SettlementMode) => void;
  setEstimate: (estimate: EngineResult | null) => void;
  addProfitLeak: (leak: ProfitLeak) => void;
  recoverLeak: (leak: ProfitLeak) => void;
  dismissLeak: (leakId: string) => void;
  dismissProfitLeak: (leakId: string) => void;
  restoreDismissedProfitLeaks: () => void;
  confirmRemoveRecommendedItem: (itemId: string) => void;
  cancelRemoveRecommendedItem: (itemId: string) => void;

  // computed (jako funkcje, nie jako selektory - proĹ›ciej)
  getKosztoryNetto: () => number;
  getFinalTotal: () => number;
  canProceed: () => boolean;

  // Tarcza MarĹĽy - walidacja technologii
  validateTechnology: () => { gaps: Gap[]; totalLost: number };
  getTotalLost: () => number;

  // AI Vision results
  setAIVisionResults: (results: {
    obwod: number;
    wysokosc: number;
    powPodlogi: number;
    powScianNetto: number;
    otworyStandardoweSzt: number;
    otworyDuzeM2: number;
    glifyMb: number;
    confidence: number;
    detectedGaps: string[];
    surfaceCondition: string;
  }) => void;

  // Domain integration - setVisionResult from AI
  setVisionResult: (data: {
    flatSurfaceM2: number;
    floorAreaM2: number;
    edgesMB: number;
    detectedMaterial?: string;
    wetZone?: boolean;
    moistureDetected?: boolean;
    confidence: number;
    detectedGaps: string[];
    surfaceCondition: string;
    otworyStandardoweSzt: number;
    otworyDuzeM2: number;
    glifyMb: number;
  }) => void;

  // Tarcza MarĹĽy - validateTech
  validateTech: () => void;
}

// ---------- STAN POCZÄ„TKOWY ----------

const initialData: ProjectData = {
  branza: null,
  tradeEngine: null,
  projectName: '',
  clientName: '',
  description: '',
  photoDataUrl: null,
  obwod: null,
  wysokosc: null,
  powPodlogi: null,
  projectMode: 'room',
  roomCount: null,
  region: 'warminsko-mazurskie',
  cityLocation: '',
  useCustomLaborRate: false,
  customLaborRateRbg: null,
  length: null,
  width: null,
  height: null,
  powScianNetto: null,
  otworyStandardoweSzt: null,
  otworyDuzeM2: null,
  standardWindowCount: null,
  bigWindowsArea: null,
  glifyMb: null,
  doorCount: null,
  windowCount: undefined,
  aiAnalysisDone: false,
  aiConfidence: 0,
  aiDetectedGaps: [],
  surfaceCondition: 'unknown',
  isOutdoor: false,
  bathroomTilesAreaM2: null,
  bathroomHydroAreaM2: null,
  bathroomWaterPoints: null,
  bathroomElectricPoints: null,
  bathroomDemolitionItems: null,
  bathroomGrooveLengthM: null,
  bathroomTileRemovalAreaM2: null,
  pavingAreaM2: null,
  pavingStoneThicknessCm: null,
  pavingEdgeLengthMb: null,
  pavingCurbLengthMb: null,
  pavingExcavationDepthCm: null,
  pavingSubbaseThicknessCm: null,
  pavingBeddingThicknessCm: null,
  pavingWasteRemovalM3: null,
  pavingCuttingLengthMb: null,
  pavingHasGeotextile: false,
  pavingMachineAccess: false,
  pavingCrewSize: null,
  edgeLengthM: null,
  baseThicknessCm: null,
  beddingThicknessCm: null,
  crewSize: null,
  requiresRB: false,
  rbHours: null,
  estimate: null,
  currentEstimate: null,
  profitLeaks: [],
  dismissedProfitLeakIds: [],
  pendingRecommendedRemoval: null,
  businessRules: [],
  totalLossAmount: 0,
  items: [],
  detectedGaps: [],
  appliedGapIds: [],
  margePreset: 'standard',
  margeValue: 0,
  laborRateMultiplier: 1,
  aiAdded: 0,
  selectedPriceRegion: 'MVP_AVERAGE',
  userMaterialOverrides: {},
  materialMode: 'CLIENT_MATERIALS',
  settlementMode: 'no_vat',
};

function isInitialProjectState(state: ProjectStore): boolean {
  return (
    state.step === 1 &&
    state.branza === initialData.branza &&
    state.tradeEngine === initialData.tradeEngine &&
    state.projectName === initialData.projectName &&
    state.clientName === initialData.clientName &&
    state.description === initialData.description &&
    state.photoDataUrl === initialData.photoDataUrl &&
    state.projectMode === initialData.projectMode &&
    state.roomCount === initialData.roomCount &&
    state.region === initialData.region &&
    state.cityLocation === initialData.cityLocation &&
    state.useCustomLaborRate === initialData.useCustomLaborRate &&
    state.customLaborRateRbg === initialData.customLaborRateRbg &&
    state.length === initialData.length &&
    state.width === initialData.width &&
    state.height === initialData.height &&
    state.powPodlogi === initialData.powPodlogi &&
    state.bigWindowsArea === initialData.bigWindowsArea &&
    state.doorCount === initialData.doorCount &&
    state.currentEstimate === initialData.currentEstimate &&
    state.estimate === initialData.estimate &&
    state.profitLeaks.length === 0 &&
    state.dismissedProfitLeakIds.length === 0 &&
    state.pendingRecommendedRemoval === initialData.pendingRecommendedRemoval &&
    state.items.length === 0 &&
    state.detectedGaps.length === 0 &&
    state.appliedGapIds.length === 0 &&
    state.aiAdded === initialData.aiAdded &&
    state.bathroomTilesAreaM2 === initialData.bathroomTilesAreaM2 &&
    state.bathroomHydroAreaM2 === initialData.bathroomHydroAreaM2 &&
    state.bathroomWaterPoints === initialData.bathroomWaterPoints &&
    state.bathroomElectricPoints === initialData.bathroomElectricPoints &&
    state.pavingAreaM2 === initialData.pavingAreaM2 &&
    state.pavingEdgeLengthMb === initialData.pavingEdgeLengthMb &&
    state.userMaterialOverrides &&
    Object.keys(state.userMaterialOverrides).length === 0
  );
}

// ---------- STORE ----------

export const useProjectStore = create<ProjectStore>((set, get) => ({
  ...initialData,
  step: 1,
  isAIAvailable: true,

  // nawigacja
  goNext: () => {
    const { step, projectMode, height } = get();
    if (step < 5) {
      set({
        step: (step + 1) as WizardStep,
        ...(step === 2 && projectMode === 'apartment' && !height
          ? { height: 2.6, wysokosc: 2.6 }
          : {}),
      });
    }
  },
  goBack: () => {
    const { step } = get();
    if (step > 1) {
      const newStep = (step - 1) as WizardStep;
      if (step === 3) {
        // CzyĹ›Ä‡ koszyk przy powrocie do Step2 â€” wymusi przeliczenie po zmianach geometrii
        set({ step: newStep, items: [], detectedGaps: [], appliedGapIds: [] });
      } else if (step === 4) {
        // CzyĹ›Ä‡ koszyk przy powrocie z Step4 do Step3 â€” wymusi recalc
        set({ step: newStep, items: [] });
      } else {
        set({ step: newStep });
      }
    }
  },
  goTo: (step) => set({ step }),
  reset: () =>
    set((state) => {
      if (isInitialProjectState(state)) {
        return state;
      }

      return { ...initialData, step: 1, isAIAvailable: state.isAIAvailable };
    }),

  // AI
  setAIAvailable: (v) => set({ isAIAvailable: v }),

  // step 1
  setBranza: (b) => {
    const tradeEngineMap: Record<Exclude<Branza, null>, TradeEngine> = {
      malarz: 'painting',
      lazienka: 'bathroom',
      brukarstwo: 'paving',
      ogolnobudowlany: 'general',
    };

    set({ branza: b, tradeEngine: b ? tradeEngineMap[b] : null });
  },

  // step 2
  setProjectName: (v) => {
    console.log('DEBUG STORE: setProjectName', v);
    set({ projectName: v });
  },
  setClientName: (v) => {
    console.log('DEBUG STORE: setClientName', v);
    set({ clientName: v });
  },
  setDescription: (v) => {
    console.log('DEBUG STORE: setDescription', v);
    set({ description: v });
  },
  setPhoto: (dataUrl) => {
    console.log('DEBUG STORE: setPhoto', !!dataUrl);
    set({ photoDataUrl: dataUrl });
  },
  setGeometry: (g) => {
    console.log('DEBUG STORE: setGeometry', g);
    console.log('DEBUG STORE: MAGAZYN DANYCH:', get());
    set((s) => ({ ...s, ...g }));
  },
  setBathroomData: (data) => set((state) => ({ ...state, ...data })),
  setPavingData: (data) => set((state) => ({ ...state, ...data })),
  setAiAnalysisDone: (v) => {
    console.log('DEBUG STORE: setAiAnalysisDone', v);
    set({ aiAnalysisDone: v });
  },

  // step 3
  setItems: (items) => set({ items }),
  addItem: (item) => set((s) => ({ items: [...s.items, item] })),
  removeItem: (id) =>
    set((state) => {
      const estimateItems = state.currentEstimate?.items ?? state.items;
      const item = estimateItems.find((current) => current.id === id);

      if (!item) {
        return state;
      }

      const warning = getRecommendedRemovalWarning({
        item,
        items: estimateItems,
        branza: state.branza,
      });

      if (warning) {
        return {
          pendingRecommendedRemoval: warning,
        };
      }

      const nextEstimate = state.currentEstimate
        ? {
            ...state.currentEstimate,
            items: state.currentEstimate.items.filter((current) => current.id !== id),
            cost: Math.max(0, state.currentEstimate.cost - item.totalNetto),
          }
        : state.currentEstimate;

      return {
        items: state.items.filter((current) => current.id !== id),
        currentEstimate: nextEstimate,
      };
    }),
  setDetectedGaps: (gaps) => set({ detectedGaps: gaps }),

  applyGap: (gapId) => {
    const { detectedGaps, items, appliedGapIds } = get();
    if (appliedGapIds.includes(gapId)) return;
    const gap = detectedGaps.find((g) => g.id === gapId);
    if (!gap) return;

    const newItem: EstimateItem = {
      ...gap.autoFixItem,
      id: `autofix_${gapId}_${Date.now()}`,
      isAutoFix: true,
    };
    set({
      items: [...items, newItem],
      appliedGapIds: [...appliedGapIds, gapId],
    });
  },

  dismissGap: (gapId) => {
    set((s) => ({
      detectedGaps: s.detectedGaps.filter((g) => g.id !== gapId),
    }));
  },

  // step 4
  setMargePreset: (p) => {
    set((state) => {
      const previousMultiplier = state.laborRateMultiplier || 1;
      const nextMultiplier = getLaborRateMultiplier(p);
      const adjustedEstimateItems = state.currentEstimate
        ? applyLaborRateMultiplier(state.currentEstimate.items, previousMultiplier, nextMultiplier)
        : [];
      const nextEstimate = state.currentEstimate
        ? {
            ...state.currentEstimate,
            items: adjustedEstimateItems,
            cost: sumMoney(adjustedEstimateItems.map((item) => item.totalNetto)),
          }
        : state.currentEstimate;

      return {
        margePreset: p,
        margeValue: 0,
        laborRateMultiplier: nextMultiplier,
        currentEstimate: nextEstimate,
        items: applyLaborRateMultiplier(state.items, previousMultiplier, nextMultiplier),
      };
    });
  },
  setMargeValue: () => set({ margeValue: 0 }),
  setAiAdded: (value) =>
    set((state) => ({
      aiAdded: typeof value === 'function' ? value(state.aiAdded) : value,
    })),
  setPriceRegion: (region) => set({ selectedPriceRegion: region }),
  setMaterialOverride: (materialId, price) =>
    set((state) => ({
      userMaterialOverrides: {
        ...state.userMaterialOverrides,
        [materialId]: price,
      },
    })),
  clearMaterialOverride: (materialId) =>
    set((state) => {
      const nextOverrides = { ...state.userMaterialOverrides };
      delete nextOverrides[materialId];
      return { userMaterialOverrides: nextOverrides };
    }),
  setMaterialMode: (mode) => set({ materialMode: mode }),
  setSettlementMode: (mode) => set({ settlementMode: mode }),
  setEstimate: (estimate) =>
    set((state) => ({
      currentEstimate: estimate,
      items: estimate?.items ?? [],
      profitLeaks: (estimate?.leaks ?? []).map((leak) =>
        state.dismissedProfitLeakIds.includes(leak.id)
          ? { ...leak, dismissed: true, decisionStatus: leak.decisionStatus ?? 'dismissed' }
          : leak
      ),
    })),
  addProfitLeak: (leak) =>
    set((state) => {
      if (state.profitLeaks.some((current) => current.id === leak.id)) {
        return {
          profitLeaks: state.profitLeaks.map((current) =>
            current.id === leak.id
              ? {
                  ...leak,
                  aiAdded: current.aiAdded,
                  dismissed: current.dismissed,
                  decisionStatus: current.decisionStatus ?? leak.decisionStatus ?? 'recommended',
                  userDecision: current.userDecision,
                }
              : current
          ),
        };
      }

      return {
        profitLeaks: [...state.profitLeaks, { ...leak, decisionStatus: leak.decisionStatus ?? 'recommended' }],
      };
    }),
  recoverLeak: (leak) =>
    set((state) => {
      if (leak.aiAdded) {
        return state;
      }

      if (!leak.suggestedItem) {
        return {
          dismissedProfitLeakIds: state.dismissedProfitLeakIds.includes(leak.id)
            ? state.dismissedProfitLeakIds
            : [...state.dismissedProfitLeakIds, leak.id],
          profitLeaks: state.profitLeaks.map((current) =>
            current.id === leak.id ? { ...current, aiAdded: true, decisionStatus: 'accepted' } : current
          ),
        };
      }

      if (state.items.some((item) => item.id === leak.suggestedItem?.id)) {
        return {
          dismissedProfitLeakIds: state.dismissedProfitLeakIds.includes(leak.id)
            ? state.dismissedProfitLeakIds
            : [...state.dismissedProfitLeakIds, leak.id],
          profitLeaks: state.profitLeaks.map((current) =>
            current.id === leak.id ? { ...current, aiAdded: true, decisionStatus: 'accepted' } : current
          ),
        };
      }

      const recoveredItem: EstimateItem = {
        id: leak.suggestedItem.id,
        label: leak.suggestedItem.label,
        quantity: leak.suggestedItem.quantity,
        unit: leak.suggestedItem.unit,
        pricingMode: leak.suggestedItem.pricingMode,
        unitPrice: leak.suggestedItem.unitPrice,
        total: leak.suggestedItem.total,
        unitRateNetto: leak.suggestedItem.unitRateNetto,
        totalNetto: leak.suggestedItem.totalNetto,
        calculation: leak.suggestedItem.calculation,
        isAutoFix: true,
        category: leak.suggestedItem.category,
      };

      const nextEstimate = state.currentEstimate
        ? {
            ...state.currentEstimate,
            items: [...state.currentEstimate.items, recoveredItem],
            cost: sumMoney([state.currentEstimate.cost, recoveredItem.totalNetto]),
          }
        : state.currentEstimate;

      return {
        items: [...state.items, recoveredItem],
        aiAdded: sumMoney([state.aiAdded, leak.value]),
        currentEstimate: nextEstimate,
        dismissedProfitLeakIds: state.dismissedProfitLeakIds.includes(leak.id)
          ? state.dismissedProfitLeakIds
          : [...state.dismissedProfitLeakIds, leak.id],
        profitLeaks: state.profitLeaks.map((current) =>
          current.id === leak.id ? { ...current, aiAdded: true, decisionStatus: 'accepted' } : current
        ),
      };
    }),
  dismissLeak: (leakId) =>
    set((state) => ({
      profitLeaks: state.profitLeaks.map((leak) =>
        leak.id === leakId ? { ...leak, dismissed: true, decisionStatus: 'dismissed' } : leak
      ),
    })),
  dismissProfitLeak: (leakId) =>
    set((state) => ({
      dismissedProfitLeakIds: state.dismissedProfitLeakIds.includes(leakId)
        ? state.dismissedProfitLeakIds
        : [...state.dismissedProfitLeakIds, leakId],
      profitLeaks: state.profitLeaks.map((leak) =>
        leak.id === leakId ? { ...leak, dismissed: true, decisionStatus: 'dismissed' } : leak
      ),
      currentEstimate: state.currentEstimate
        ? {
            ...state.currentEstimate,
            leaks: state.currentEstimate.leaks.map((leak) =>
              leak.id === leakId ? { ...leak, dismissed: true, decisionStatus: 'dismissed' } : leak
            ),
          }
        : state.currentEstimate,
    })),
  restoreDismissedProfitLeaks: () =>
    set((state) => ({
      dismissedProfitLeakIds: [],
      profitLeaks: state.profitLeaks.map((leak) =>
        leak.aiAdded ? leak : { ...leak, dismissed: false, decisionStatus: 'recommended' }
      ),
      currentEstimate: state.currentEstimate
        ? {
            ...state.currentEstimate,
            leaks: state.currentEstimate.leaks.map((leak) =>
              leak.aiAdded ? leak : { ...leak, dismissed: false, decisionStatus: 'recommended' }
            ),
          }
        : state.currentEstimate,
    })),
  confirmRemoveRecommendedItem: (itemId) =>
    set((state) => {
      const item = state.currentEstimate?.items.find((current) => current.id === itemId);

      if (!item) {
        return {
          pendingRecommendedRemoval: null,
        };
      }

      const timestamp = Date.now();
      const leakId = `removed_${item.id}`;
      const existingLeak = state.profitLeaks.find(
        (leak) => leak.suggestedItem?.id === item.id || leak.id === leakId
      );
      const removedLeak: ProfitLeak = existingLeak
        ? {
            ...existingLeak,
            aiAdded: false,
            dismissed: true,
            decisionStatus: 'removed_with_risk',
            userDecision: { action: 'removed_with_risk', timestamp },
          }
        : {
            id: leakId,
            type: item.category === 'weather_protection' ? 'weather_protection' : 'custom',
            severity: 'critical',
            label: item.label,
            message: 'Pozycja technologiczna pominiÄ™ta decyzjÄ… wykonawcy.',
            clientPdfMessage: 'Pozycja technologiczna pominiÄ™ta decyzjÄ… wykonawcy.',
            value: item.totalNetto,
            quantity: item.quantity,
            unit: item.unit,
            unitRate: item.unitRateNetto,
            calculation:
              item.calculation ??
              `${item.quantity} ${item.unit} Ă— ${item.unitRateNetto} zĹ‚/${item.unit} = ${item.totalNetto} zĹ‚`,
            aiAdded: false,
            dismissed: true,
            decisionStatus: 'removed_with_risk',
            userDecision: { action: 'removed_with_risk', timestamp },
            suggestedItem: item,
          };

      const nextEstimate = state.currentEstimate
        ? {
            ...state.currentEstimate,
            items: state.currentEstimate.items.filter((current) => current.id !== itemId),
            cost: Math.max(0, state.currentEstimate.cost - item.totalNetto),
          }
        : state.currentEstimate;
      const leakExists = state.profitLeaks.some((leak) => leak.id === removedLeak.id);

      return {
        items: state.items.filter((current) => current.id !== itemId),
        currentEstimate: nextEstimate,
        pendingRecommendedRemoval: null,
        profitLeaks: leakExists
          ? state.profitLeaks.map((leak) => (leak.id === removedLeak.id ? removedLeak : leak))
          : [...state.profitLeaks, removedLeak],
      };
    }),
  cancelRemoveRecommendedItem: (itemId) =>
    set((state) => ({
      pendingRecommendedRemoval:
        state.pendingRecommendedRemoval?.itemId === itemId ? null : state.pendingRecommendedRemoval,
      profitLeaks: state.profitLeaks.map((leak) =>
        leak.suggestedItem?.id === itemId
          ? {
              ...leak,
              decisionStatus: 'accepted',
              userDecision: { action: 'accepted', timestamp: Date.now() },
            }
          : leak
      ),
    })),

  // computed
  getKosztoryNetto: () => {
    const state = get();
    return state.currentEstimate?.cost ?? sumMoney(state.items.map((item) => item.totalNetto));
  },

  getFinalTotal: () => {
    const kosztory = get().getKosztoryNetto();
    return kosztory;
  },

  canProceed: () => {
    const s = get();
    switch (s.step) {
      case 1:
        return s.branza !== null;
      case 2:
        if (s.tradeEngine === 'bathroom' || s.branza === 'lazienka') {
          return (
            (s.bathroomTilesAreaM2 ?? 0) > 0 ||
            (s.bathroomHydroAreaM2 ?? 0) > 0 ||
            (s.bathroomWaterPoints ?? 0) > 0 ||
            (s.bathroomElectricPoints ?? 0) > 0 ||
            (s.bathroomTileRemovalAreaM2 ?? 0) > 0 ||
            (s.bathroomGrooveLengthM ?? 0) > 0 ||
            (s.bathroomDemolitionItems ?? 0) > 0
          );
        }
        if (s.tradeEngine === 'paving' || s.branza === 'brukarstwo') {
          return (s.pavingAreaM2 ?? 0) > 0;
        }
        if (s.projectMode === 'apartment') {
          return (s.powPodlogi ?? 0) > 0;
        }
        return (s.length ?? 0) > 0 && (s.width ?? 0) > 0 && (s.height ?? 0) > 0;
      case 3:
        return s.items.length > 0 || (s.currentEstimate?.items.length ?? 0) > 0;
      case 4:
        return s.items.length > 0 || (s.currentEstimate?.items.length ?? 0) > 0;
      case 5:
        return true;
      default:
        return false;
    }
  },

  // TARCZA MARĹ»Y - WALIDACJA TECHNOLOGII
  validateTechnology: () => {
    const s = get();
    const gaps: Gap[] = [];
    let totalLost = 0;

    // SprawdĹş brak gruntu przy malowaniu/gĹ‚adzi
    const hasGrunt = s.items.some(i => i.category === 'grunt' || i.label.toLowerCase().includes('grunt'));
    const hasGladz = s.items.some(i => i.category === 'gladz' || i.label.toLowerCase().includes('gĹ‚adĹş'));
    const hasMalowanie = s.items.some(i => i.category === 'malowanie' || i.label.toLowerCase().includes('malowanie'));

    if ((hasGladz || hasMalowanie) && !hasGrunt) {
      const gap: Gap = {
        id: 'missing_grunt',
        severity: 'critical',
        title: 'đź”´ Gruntowanie do sprawdzenia',
        reason: 'GoĹ‚y tynk = farba odparzy w 2 lata. Koszt naprawy ~5000 PLN. Stracisz klienta i reputacjÄ™.',
        autoFixLabel: 'DOLICZ POZYCJE (+520 PLN)',
        autoFixAmount: 520,
        autoFixItem: {
          label: 'Gruntowanie (zapobiega odpryskom)',
          quantity: s.powScianNetto || 20,
          unit: 'mÂ˛',
          pricingMode: 'area',
          unitRateNetto: 8,
          unitPrice: 8,
          totalNetto: (s.powScianNetto || 20) * 8,
          total: (s.powScianNetto || 20) * 8,
          calculation: `${s.powScianNetto || 20} mÂ˛ Ă— 8 zĹ‚/mÂ˛ = ${(s.powScianNetto || 20) * 8} zĹ‚`,
          category: 'grunt',
        },
      };
      gaps.push(gap);
      totalLost += gap.autoFixAmount;
    }

    // SprawdĹş hydroizolacjÄ™ przy Ĺ‚azience
    const isLazienka = s.branza === 'lazienka';
    const hasHydro = s.items.some(i => i.category === 'hydro' || i.label.toLowerCase().includes('hydro'));

    if (isLazienka && !hasHydro) {
      const gap: Gap = {
        id: 'missing_hydro',
        severity: 'critical',
        title: 'đź”´ JesteĹ› 380 PLN w plecy! Brak hydroizolacji',
        reason: 'PĹ‚ytki bez hydroizolacji = wilgoÄ‡ w Ĺ›cianie. Koszt poprawki: 380 PLN materiaĹ‚ + 1200 PLN robocizna.',
        autoFixLabel: 'DODAJ HYDROIZOLACJÄ (+380 PLN)',
        autoFixAmount: 380,
        autoFixItem: {
          label: 'Hydroizolacja Ĺ‚azienkowa (folia w pĹ‚ynie)',
          quantity: s.powPodlogi || 5,
          unit: 'mÂ˛',
          pricingMode: 'area',
          unitRateNetto: 35,
          unitPrice: 35,
          totalNetto: (s.powPodlogi || 5) * 35,
          total: (s.powPodlogi || 5) * 35,
          calculation: `${s.powPodlogi || 5} mÂ˛ Ă— 35 zĹ‚/mÂ˛ = ${(s.powPodlogi || 5) * 35} zĹ‚`,
          category: 'hydro',
        },
      };
      gaps.push(gap);
      totalLost += gap.autoFixAmount;
    }

    // SprawdĹş glify (naroĹĽniki) - +15% do robocizny
    const glifyMb = s.glifyMb || 0;
    if (glifyMb > 0) {
      const gap: Gap = {
        id: 'missing_glify',
        severity: 'warning',
        title: 'âš ď¸Ź PominiÄ™to szlify 45Â°!',
        reason: `KaĹĽdy naroĹĽnik okienny to +15% czasu na mb. Masz ${glifyMb.toFixed(1)}mb glifĂłw - to +${(glifyMb * 0.15 * 45).toFixed(0)} PLN kosztu.`,
        autoFixLabel: `DODAJ GLIFY (+${(glifyMb * 0.15 * 45).toFixed(0)} PLN)`,
        autoFixAmount: glifyMb * 0.15 * 45,
        autoFixItem: {
          label: 'Szlify naroĹĽnikĂłw 45Â° (dodatkowy nakĹ‚ad pracy)',
          quantity: glifyMb,
          unit: 'mb',
          pricingMode: 'linear',
          unitRateNetto: 45 * 0.15,
          unitPrice: 45 * 0.15,
          totalNetto: glifyMb * 0.15 * 45,
          total: glifyMb * 0.15 * 45,
          calculation: `${glifyMb} mb Ă— ${45 * 0.15} zĹ‚/mb = ${glifyMb * 0.15 * 45} zĹ‚`,
          category: 'robocizna',
        },
      };
      gaps.push(gap);
      totalLost += gap.autoFixAmount;
    }

    // SprawdĹş akrylowanie - drobna pozycja, duĹĽy koszt w skali roku
    const hasAkryl = s.items.some(i => i.label.toLowerCase().includes('akryl') || i.label.toLowerCase().includes('silikon'));
    const otworyCount = (s.otworyStandardoweSzt || 0) + Math.floor((s.otworyDuzeM2 || 0) / 2);

    if (otworyCount > 0 && !hasAkryl) {
      const gap: Gap = {
        id: 'missing_akryl',
        severity: 'warning',
        title: 'âš ď¸Ź Drobna pozycja do sprawdzenia',
        reason: `Masz ${otworyCount} otworĂłw bez akrylowania. Klient dostanie fakturÄ™ za szpary od wiatru.`,
        autoFixLabel: `DODAJ AKRYL (+${otworyCount * 28} PLN)`,
        autoFixAmount: otworyCount * 28,
        autoFixItem: {
          label: 'Akrylowanie/silikonowanie otworĂłw',
          quantity: otworyCount,
          unit: 'szt',
          pricingMode: 'piece',
          unitRateNetto: 28,
          unitPrice: 28,
          totalNetto: otworyCount * 28,
          total: otworyCount * 28,
          calculation: `${otworyCount} szt Ă— 28 zĹ‚/szt = ${otworyCount * 28} zĹ‚`,
          category: 'detale',
        },
      };
      gaps.push(gap);
      totalLost += gap.autoFixAmount;
    }

    return { gaps, totalLost };
  },

  getTotalLost: () => {
    return get().validateTechnology().totalLost;
  },

  // AI Vision - set all results at once
  setAIVisionResults: (results) => {
    console.log('[Store] AI Vision results:', results);
    set({
      obwod: results.obwod,
      wysokosc: results.wysokosc,
      powPodlogi: results.powPodlogi,
      powScianNetto: results.powScianNetto,
      otworyStandardoweSzt: results.otworyStandardoweSzt,
      otworyDuzeM2: results.otworyDuzeM2,
      glifyMb: results.glifyMb,
      aiConfidence: results.confidence,
      aiDetectedGaps: results.detectedGaps,
      surfaceCondition: results.surfaceCondition,
      aiAnalysisDone: true,
    });

    // Auto-trigger Tarcza MarĹĽy validation
    get().validateTech();
  },

  // DOMAIN INTEGRATION - setVisionResult from AI Vision
  setVisionResult: (data) => {
    console.log('đź”Ť Store setVisionResult - OTRZYMAĹEM DANE:', data);
    console.log('đź”Ť Store setVisionResult - flatSurfaceM2:', data.flatSurfaceM2);
    
    // Create SpatialData from AI results
    const spatial: SpatialData = {
      flatSurfaceM2: data.flatSurfaceM2,
      floorAreaM2: data.floorAreaM2,
      edgesMB: data.edgesMB,
      wallHeightM: 2.5, // default
      perimeterM: data.edgesMB || 0,
      openingsAreaM2: 0,
      detectedMaterial: data.detectedMaterial,
      wetZone: data.wetZone ?? false,
      moistureDetected: data.moistureDetected ?? false,
      scope: 'painting', // default scope
      confidence: data.confidence,
      referenceObject: 'NONE',
      scaleFactor: 1,
      cracksDetected: false,
      doorCount: 1, // default
      windowCount: 0, // default
    };

    // AUTO-CALCULATE MARGIN based on surface area (25% of labor cost = healthy profit)
    const autoMargin = Math.round(data.flatSurfaceM2 * 38 * 0.25);
    const safeMargin = Math.max(autoMargin, 1200); // minimum 1200 PLN

    // Create Estimate domain model
    const newEstimate: Estimate = {
      id: crypto.randomUUID(),
      source: 'vision',
      spatial,
      costs: {
        materials: data.flatSurfaceM2 * 12, // Twoja Ĺ›rednia rynkowa
        labor: data.flatSurfaceM2 * 38,    // Twoja stawka bazowa
        margin: safeMargin,                // AUTO-CALCULATED PROFIT
        riskBuffer: data.flatSurfaceM2 * 5, // 5 PLN/m2 buffer
        total: (data.flatSurfaceM2 * 12) + (data.flatSurfaceM2 * 38) + safeMargin + (data.flatSurfaceM2 * 5),
      },
      rules: [],
      meta: {
        createdAt: Date.now(),
        updatedAt: Date.now(),
        aiConfidence: data.confidence,
        scopeDetected: 'painting',
      },
    };

    // Run AI-based validation
    const aiRules = validateFromAIVision(
      spatial,
      data.detectedGaps,
      data.surfaceCondition,
      data.otworyStandardoweSzt,
      data.otworyDuzeM2,
      data.glifyMb,
    );

    newEstimate.rules = aiRules;

    const totalLoss = calculateTotalLoss(aiRules);

    // Auto-set margin value based on calculated margin from AI
    const calculatedMargin = newEstimate.costs.margin;

    set({
      estimate: newEstimate,
      businessRules: aiRules,
      totalLossAmount: totalLoss,
      aiConfidence: data.confidence,
      aiDetectedGaps: data.detectedGaps,
      surfaceCondition: data.surfaceCondition,
      margeValue: 0,
    });

    console.log('[Store] setVisionResult - Domain estimate created:', newEstimate);
    console.log('[Store] Tarcza MarĹĽy - Total loss:', totalLoss);
  },

  // Tarcza MarĹĽy - validateTech (re-run validation)
  validateTech: () => {
    const { estimate, aiDetectedGaps, surfaceCondition, otworyStandardoweSzt, otworyDuzeM2, glifyMb, powScianNetto } = get();

    if (!estimate || !estimate.spatial) {
      console.log('[Store] validateTech - no estimate to validate');
      return;
    }

    // Re-run validation with current data
    const spatial = estimate.spatial;
    spatial.flatSurfaceM2 = powScianNetto || spatial.flatSurfaceM2;

    const rules = validateFromAIVision(
      spatial,
      aiDetectedGaps,
      surfaceCondition,
      otworyStandardoweSzt || 0,
      otworyDuzeM2 || 0,
      glifyMb || 0,
    );

    const totalLoss = calculateTotalLoss(rules);

    set({
      estimate: { ...estimate, rules },
      businessRules: rules,
      totalLossAmount: totalLoss,
    });

    console.log('[Store] validateTech - Rules updated:', rules.length, 'Total loss:', totalLoss);
  },
}));
